import { graphql } from "react-relay";

/* Note on recurrenceRules {
  timeZone
},
Used by AutopilotFormGcal -> TimingSet to determine whether the event is recurring.
*/

export const eventFragments = {
  org: graphql`
    fragment Event_org on Org {
      ...AutopilotFormSaveOnEdit_org
      id
      ...EventSummary_org
      ...EventSmartHoldInfo_org
      ...EventTeam_org
      id
      experiments
      domains
      primaryOrgCalendar
      primaryOrgEmail
      shadowCalendarSettingsErrorable {
        __typename
        ... on ShadowCalendarSettings {
          enabled
          shadowCalendarId
        }
      }
      featureGrid {
        __typename
        ... on FeatureGrid {
          stateMetadata {
            primaryBillingGroupMetadata {
              billingGroup {
                id
                invitePolicy
              }
            }
          }
        }
        ... on GraphEntityError {
          statusCode
          message
        }
      }
      orgAccountingPeriod {
        __typename
        ... on OrgAccountingPeriod {
          periodRange {
            minDate
            maxDate
          }
          quotaMax
          quotaUsed
          enforcementState
        }
        ... on GraphEntityError {
          statusCode
          message
        }
      }
    }
  `,
  eventParent: graphql`
    fragment Event_eventParent on EventParent
    @argumentDefinitions(calendarId: { type: "String!" }, isOnAPHistory: { type: "Boolean!" }) {
      id
      externalEventId
      ...EventPersonalCalendarInfo_eventParent @arguments(calendarId: $calendarId)
      ...AutopilotFormSaveOnEdit_eventParent
      ...EventTeam_eventParent @arguments(calendarId: $calendarId)
      ...EventSummary_eventParent
      syncedOriginEventErrorable {
        ... on Event {
          ...EventSummary_event @arguments(calendarId: $calendarId)
          title
          description
          descriptionOmitted
          locked
          isAllDay
          isClockwiseEvent
          createdMillis
          updatedMillis
          hangoutLink
          organizer {
            urnValue
          }
          eventKey {
            externalEventId
          }
          annotatedEvent {
            orgTags {
              tag
              state {
                subjectType
                subjectValue
                value
              }
            }
          }
          attendees {
            ...EventSummaryRoom_attendees
            organizer
            displayName
            urnValue
          }
          startTime {
            millis
          }
          endTime {
            millis
          }
          recurrenceRules {
            firstInstanceStartTime {
              type
              dateOrDateTime
              timeZone
              millis
              offsetMinutes
            }
            timeZone
            interval {
              interval
              byDay
              bySetPos
              freqType
              count
              untilTime {
                type
                dateOrDateTime
                timeZone
                millis
                offsetMinutes
              }
            }
          }
        }
        ... on GraphEntityError {
          message
          statusCode
        }
      }
      events {
        ...AutopilotFormSaveOnEdit_event @arguments(calendarId: $calendarId)
        ...EventSummary_event @arguments(calendarId: $calendarId)
        ...EventSmartHoldInfo_event @arguments(calendarId: $calendarId)
        ...EventPeople_event
        ...AutopilotEventHistory_event @arguments(calendarId: $calendarId)
        calendarIds
        isAllDay
        isClockwiseEvent
        locked
        scrubbedEvent
        organizerPerson {
          ... on Person {
            emails
            isYou
            primaryEmail
            userId
          }
        }
        organizerOrgPerson {
          ... on OrgPerson {
            isYou
            userId
            personId
            primaryCalendarId
          }
        }
        annotatedEvent {
          userTags {
            tag
            states {
              value
              subjectType
              subjectValue
              lastModified
              active
            }
          }
          orgTags {
            tag
            state {
              subjectType
              subjectValue
              value
              active
            }
          }
        }
        eventKey {
          id
          calendarIds
          externalEventId
          hashedCalendars
          orgId
        }
        startTime {
          millis
          dateOrDateTime
          timeZone
        }
        endTime {
          millis
        }
        title
        attendees {
          displayName
          urnValue
        }
        organizer {
          urnValue
        }
        attendeePeople {
          __typename
          ... on PersonList {
            list {
              id
              emails
              primaryEmail
              profile {
                givenName
                familyName
                externalImageUrl
              }
              isYou
            }
          }
        }
        recurrenceRules {
          timeZone
        }
        suggestedAutopilotSettingsResponseV3 {
          __typename
          ... on SuggestedAutopilotSettingsResponseV3 {
            intersectionOfAttendeesMeetingHours {
              minSlot {
                dayOffset
                timeSlot
              }
              maxSlot {
                dayOffset
                timeSlot
              }
            }
            calendarIdToMeetingHours {
              calendarId
              dayOffsetTimeSlotRanges {
                minSlot {
                  dayOffset
                  timeSlot
                }
                maxSlot {
                  dayOffset
                  timeSlot
                }
              }
            }
            suggestedAutopilotSettings {
              isRoomFlexible
              movementType
              allowedEndTime {
                dayOffset
                timeSlot
              }
              allowedStartTime {
                dayOffset
                timeSlot
              }
            }
          }
        }
        moveRangeInLocalTimeV3 {
          __typename
          ... on MoveRangeInLocalTimeV3 {
            allowedStartTimeInclusive {
              dayOffset
              timeSlot
            }
            allowedEndTimeInclusive {
              dayOffset
              timeSlot
            }
          }
        }
        featureStatesForOrganizer {
          __typename
          ... on FeatureStates {
            mixedFreemiumOrgUIState {
              autopilotPaused
              moveRangesEnabled
              quotaStatusBanner
            }
          }
          ... on GraphEntityError {
            statusCode
            message
          }
        }
      }
    }
  `,
  user: graphql`
    fragment Event_user on User {
      emails
      id
      ...EventSummary_user
      ...EventSmartHoldInfo_user
      ...EventTeam_user
      ...AutopilotFormSaveOnEdit_user
    }
  `,
  smartHoldEventInfo: graphql`
    fragment Event_smartHoldEventInfo on SmartHoldEventInfo {
      ...EventSmartHoldInfo_smartHoldEventInfo
      ...EventTeam_smartHoldEventInfo
      ...EventSummary_smartHoldEventInfo
    }
  `,
};
